/**
 * Common UI components and utilities
 */
class UIComponents {
    /**
     * Create a loading spinner
     * @param {string} message - Loading message
     * @returns {HTMLElement} Loading spinner element
     */
    static createLoadingSpinner(message = '加载中...') {
        const spinner = document.createElement('div');
        spinner.className = 'loading-spinner-container';
        spinner.innerHTML = `
            <div class="loading-spinner"></div>
            <div class="loading-message">${message}</div>
        `;
        return spinner;
    }

    /**
     * Create a modal dialog
     * @param {string} title - Modal title
     * @param {string} content - Modal content
     * @param {Object} options - Modal options
     * @returns {HTMLElement} Modal element
     */
    static createModal(title, content, options = {}) {
        const modal = document.createElement('div');
        modal.className = 'modal-overlay';
        modal.innerHTML = `
            <div class="modal-dialog">
                <div class="modal-header">
                    <h3 class="modal-title">${title}</h3>
                    <button class="modal-close" onclick="this.closest('.modal-overlay').remove()">×</button>
                </div>
                <div class="modal-body">
                    ${content}
                </div>
                <div class="modal-footer">
                    ${options.showOkButton !== false ? '<button class="btn btn-primary modal-ok">确定</button>' : ''}
                    ${options.showCancelButton !== false ? '<button class="btn btn-secondary modal-cancel">取消</button>' : ''}
                </div>
            </div>
        `;

        // Add event listeners
        const okButton = modal.querySelector('.modal-ok');
        const cancelButton = modal.querySelector('.modal-cancel');
        
        if (okButton && options.onOk) {
            okButton.addEventListener('click', () => {
                options.onOk();
                modal.remove();
            });
        }
        
        if (cancelButton && options.onCancel) {
            cancelButton.addEventListener('click', () => {
                options.onCancel();
                modal.remove();
            });
        }

        return modal;
    }

    /**
     * Create a progress bar
     * @param {number} value - Progress value (0-100)
     * @param {string} label - Progress label
     * @returns {HTMLElement} Progress bar element
     */
    static createProgressBar(value = 0, label = '') {
        const progress = document.createElement('div');
        progress.className = 'progress-container';
        progress.innerHTML = `
            <div class="progress-label">${label}</div>
            <div class="progress-bar">
                <div class="progress-fill" style="width: ${value}%"></div>
            </div>
            <div class="progress-text">${value}%</div>
        `;
        return progress;
    }

    /**
     * Update progress bar
     * @param {HTMLElement} progressBar - Progress bar element
     * @param {number} value - New progress value
     * @param {string} label - New label (optional)
     */
    static updateProgressBar(progressBar, value, label = null) {
        const fill = progressBar.querySelector('.progress-fill');
        const text = progressBar.querySelector('.progress-text');
        const labelElement = progressBar.querySelector('.progress-label');

        if (fill) fill.style.width = `${value}%`;
        if (text) text.textContent = `${value}%`;
        if (label && labelElement) labelElement.textContent = label;
    }

    /**
     * Create a notification toast
     * @param {string} message - Notification message
     * @param {string} type - Notification type (success, error, warning, info)
     * @param {number} duration - Auto-hide duration in milliseconds
     * @returns {HTMLElement} Toast element
     */
    static createToast(message, type = 'info', duration = 3000) {
        const toast = document.createElement('div');
        toast.className = `toast toast-${type}`;
        
        const icons = {
            success: '✅',
            error: '❌',
            warning: '⚠️',
            info: 'ℹ️'
        };

        toast.innerHTML = `
            <div class="toast-icon">${icons[type] || icons.info}</div>
            <div class="toast-message">${message}</div>
            <button class="toast-close" onclick="this.parentElement.remove()">×</button>
        `;

        // Add to toast container or body
        let toastContainer = document.getElementById('toast-container');
        if (!toastContainer) {
            toastContainer = document.createElement('div');
            toastContainer.id = 'toast-container';
            toastContainer.className = 'toast-container';
            document.body.appendChild(toastContainer);
        }

        toastContainer.appendChild(toast);

        // Auto-hide
        if (duration > 0) {
            setTimeout(() => {
                if (toast.parentElement) {
                    toast.remove();
                }
            }, duration);
        }

        return toast;
    }

    /**
     * Create a dropdown menu
     * @param {Array} options - Array of option objects {value, text}
     * @param {string} selectedValue - Initially selected value
     * @returns {HTMLElement} Select element
     */
    static createDropdown(options, selectedValue = null) {
        const select = document.createElement('select');
        select.className = 'form-select';

        options.forEach(option => {
            const optionElement = document.createElement('option');
            optionElement.value = option.value;
            optionElement.textContent = option.text;
            if (option.value === selectedValue) {
                optionElement.selected = true;
            }
            select.appendChild(optionElement);
        });

        return select;
    }

    /**
     * Create a button with icon
     * @param {string} text - Button text
     * @param {string} icon - Icon HTML or text
     * @param {string} className - Additional CSS classes
     * @returns {HTMLElement} Button element
     */
    static createButton(text, icon = '', className = 'btn btn-primary') {
        const button = document.createElement('button');
        button.className = className;
        button.innerHTML = `
            ${icon ? `<span class="btn-icon">${icon}</span>` : ''}
            <span class="btn-text">${text}</span>
        `;
        return button;
    }

    /**
     * Create a card component
     * @param {string} title - Card title
     * @param {string} content - Card content
     * @param {string} footer - Card footer (optional)
     * @returns {HTMLElement} Card element
     */
    static createCard(title, content, footer = '') {
        const card = document.createElement('div');
        card.className = 'card';
        card.innerHTML = `
            ${title ? `<div class="card-header"><h4 class="card-title">${title}</h4></div>` : ''}
            <div class="card-body">${content}</div>
            ${footer ? `<div class="card-footer">${footer}</div>` : ''}
        `;
        return card;
    }
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
    module.exports = UIComponents;
}
